import React, { useState, useEffect } from 'react';
import { useTraffic } from '../context/TrafficContext';
import { refreshProxyCredential } from '../api/proxyAuth';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function getStoredAuth() {
    try {
        const token = localStorage.getItem('auth_token');
        const user  = localStorage.getItem('auth_user');
        return token && user ? { token, user } : null;
    } catch { return null; }
}

function authHeaders() {
    try {
        const token = localStorage.getItem('auth_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch { return {}; }
}

export default function LoginPage() {
    const { setSessionId, startNewSession: startFreshSession } = useTraffic();
    const [joinId, setJoinId] = useState('');

    const [auth, setAuth]           = useState(getStoredAuth);
    const [username, setUsername]   = useState('');
    const [password, setPassword]   = useState('');
    const [authError, setAuthError] = useState('');
    const [loading, setLoading]     = useState(false);

    // Previous saved sessions for this user
    const [savedSessions, setSavedSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);

    // Load saved sessions when authenticated
    useEffect(() => {
        if (!auth) return;
        setLoadingSessions(true);
        fetch(`${API_BASE}/api/har?limit=20&sort=createdAt`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(rows => setSavedSessions(rows || []))
            .catch(() => setSavedSessions([]))
            .finally(() => setLoadingSessions(false));
    }, [auth]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setAuthError('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password })
            });
            if (!res.ok) {
                let detail = '';
                try { const d = await res.json(); detail = d.error || d.message || JSON.stringify(d); } catch {}
                setAuthError(`Server error ${res.status}${detail ? ': ' + detail : ''}`);
                return;
            }
            const data = await res.json();
            if (data.ok && data.token) {
                localStorage.setItem('auth_token', data.token);
                localStorage.setItem('auth_user', data.username);
                // Prime the proxy credential cache right away (fire-and-forget —
                // docs/proxy-credential-spec.md) so the first proxy-bound call
                // doesn't pay for a mint round trip. Silent no-op if the backend
                // doesn't have PROXY_JWT_SECRET configured.
                refreshProxyCredential();
                // Fresh dashboard — clear any persisted live traffic from previous session
                try { localStorage.removeItem('groupedResults.slim'); } catch {}
                setAuth({ token: data.token, user: data.username });
            } else {
                setAuthError('Invalid username or password');
            }
        } catch (err) {
            setAuthError(`Connection error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('dashboard_auth');
        try { localStorage.removeItem('groupedResults.slim'); } catch {}
        setAuth(null);
    };

    const startNewSession = () => {
        // Same fresh start as the dashboard's New session button. Picking an id
        // alone kept localStorage traffic and let the proxy replay its buffer.
        if (window.confirm('Start with an empty capture?\n\nOK: clear all previously captured traffic, on the dashboard and on the proxy (cannot be undone).\nCancel: keep the previous traffic.')) {
            startFreshSession();
        }
        const newId = `Session_${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        setSessionId(newId);
    };

    const joinSession = (e) => {
        e.preventDefault();
        if (joinId.trim()) setSessionId(joinId.trim());
    };

    const openSavedSession = (id) => {
        // Opens the HAR directly in the main app via the global hook
        if (window.openHarSession) window.openHarSession(id);
        else setSessionId('live'); // fallback — just navigate to live
    };

    // ── LOGIN SCREEN ──────────────────────────────────────────────────────────
    if (!auth) {
        return (
            <div style={S.page}>
                <div style={S.card}>
                    <div style={{ fontSize: 42, marginBottom: 8 }}>📡</div>
                    <h1 style={S.title}>AdTech QA Tool</h1>
                    <p style={S.subtitle}>Sign in to access the dashboard</p>

                    <form onSubmit={handleLogin}>
                        <input
                            type="text"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            placeholder="Username"
                            autoFocus
                            autoComplete="username"
                            style={S.input}
                        />
                        <input
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="Password"
                            autoComplete="current-password"
                            style={{ ...S.input, marginTop: 10 }}
                        />
                        {authError && <div style={S.error}>{authError}</div>}
                        <button type="submit" disabled={loading || !username || !password} style={S.btn}>
                            {loading ? 'Signing in…' : 'Sign In'}
                        </button>
                    </form>

                    <div style={{ marginTop: 20, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                        Contact your admin to get access
                    </div>
                </div>
            </div>
        );
    }

    // ── SESSION PICKER ────────────────────────────────────────────────────────
    return (
        <div style={{ ...S.page, background: 'var(--bg)' }}>
            <div style={{ ...S.card, background: 'var(--card)', color: 'var(--ink)', border: '1px solid var(--line)', maxWidth: 460 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div>
                        <div style={{ fontSize: 32 }}>🛡️</div>
                        <h1 style={{ margin: '6px 0 2px', fontSize: 22, color: 'var(--ink)' }}>AdTech Validator</h1>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>Signed in as <b>{auth.user}</b></div>
                    </div>
                    <button onClick={handleLogout} style={S.logoutBtn}>Sign out</button>
                </div>

                <p style={{ color: 'var(--muted)', marginBottom: 16, fontSize: 13 }}>
                    Start a new capture session or load a saved one.
                </p>

                <button onClick={startNewSession} style={{ ...S.btn, background: 'var(--primary)' }}>
                    ▶ Start New Live Session
                </button>

                {/* Previous saved HAR sessions */}
                {(loadingSessions || savedSessions.length > 0) && (
                    <div style={{ marginTop: 20 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                            Saved Sessions
                        </div>
                        {loadingSessions ? (
                            <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 12 }}>Loading…</div>
                        ) : (
                            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
                                {savedSessions.map((s, i) => {
                                    const date = s.createdAt ? new Date(s.createdAt).toLocaleString() : '';
                                    const label = s.title || s.host || 'Session';
                                    const size = s.gzBytes ? `${(s.gzBytes / 1024).toFixed(0)} KB` : '';
                                    return (
                                        <div
                                            key={s.id}
                                            onClick={() => openSavedSession(s.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '9px 12px', cursor: 'pointer',
                                                borderBottom: i < savedSessions.length - 1 ? '1px solid var(--line)' : 'none',
                                                background: 'var(--card)',
                                                transition: 'background 0.1s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'var(--card)'}
                                        >
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {s.favorite ? '⭐ ' : ''}{label}
                                                </div>
                                                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{date}{size ? ` · ${size}` : ''}</div>
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--primary)', marginLeft: 10, flexShrink: 0 }}>Open →</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <div style={S.divider}>
                    <div style={S.dividerLine} />
                    <span style={{ color: 'var(--muted)', fontSize: 11, padding: '0 10px' }}>OR JOIN EXISTING</span>
                    <div style={S.dividerLine} />
                </div>

                <form onSubmit={joinSession}>
                    <input
                        value={joinId}
                        onChange={e => setJoinId(e.target.value)}
                        placeholder="Session ID (e.g. Session_PMXJ)"
                        style={{ ...S.input, background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line)' }}
                    />
                    <button type="submit" disabled={!joinId.trim()}
                        style={{ ...S.btn, background: 'var(--card)', color: 'var(--ink)', border: '1px solid var(--line)', marginTop: 8, opacity: joinId.trim() ? 1 : 0.5 }}>
                        Join Session
                    </button>
                </form>
            </div>
        </div>
    );
}

const S = {
    page: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: '#1a1f2e',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    },
    card: {
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
        backdropFilter: 'blur(12px)',
        borderRadius: 16, padding: '36px 40px',
        width: '100%', maxWidth: 360,
        textAlign: 'center', color: 'white',
        boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
    },
    title: { margin: '0 0 6px', fontSize: 24, fontWeight: 700 },
    subtitle: { color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 24 },
    input: {
        width: '100%', padding: '11px 14px', borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.15)',
        background: 'rgba(0,0,0,0.25)', color: 'white',
        fontSize: 15, outline: 'none', boxSizing: 'border-box',
    },
    error: { color: '#f87171', fontSize: 12, margin: '8px 0', textAlign: 'left' },
    btn: {
        width: '100%', padding: '12px', marginTop: 14,
        background: '#007aff', color: 'white',
        border: 'none', borderRadius: 8,
        fontSize: 15, fontWeight: 600, cursor: 'pointer',
    },
    logoutBtn: {
        padding: '6px 12px', background: 'transparent',
        border: '1px solid var(--line)', borderRadius: 6,
        fontSize: 12, cursor: 'pointer', color: 'var(--muted)',
    },
    divider: { display: 'flex', alignItems: 'center', margin: '20px 0' },
    dividerLine: { flex: 1, height: 1, background: 'var(--line)' },
};
